import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiCamera, FiPlay, FiGlobe, FiUsers } from 'react-icons/fi';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';
import '../css/services.css';

const Services = () => {
  const [openSection, setOpenSection] = useState(0);

  const services = [
    {
      id: 0,
      title: 'Live Sports Production & Streaming',
      icon: <FiPlay className="w-8 h-8" />,
      description: 'From local matches to regional tournaments, we deliver seamless live sports coverage: multicamera shoot, commentary, replays, graphics overlays, live stats, and real-time streaming. You supply the content—we make it broadcast-ready.',
      image: '/Barbados Home of Cricket Legends/WhatsApp Image 2025-09-24 at 6.10.44 PM.jpeg',
      capabilities: [
        'Multi-Camera Live Production',
        'Real-Time Commentary',
        'Instant Replays',
        'Graphics Overlays',
        'Live Statistics',
        'Real-Time Streaming',
        'Sports League Coverage',
        'Federation Partnerships',
        'Sponsor Integration',
        'Broadcaster Services'
      ]
    },
    {
      id: 1,
      title: 'Television, Series & Branded Content',
      icon: <FiCamera className="w-8 h-8" />,
      description: 'We produce scripted series, talk shows, reality formats, lifestyle shows, and branded content. From concept to final delivery, we manage everything—writing, directing, shooting, post, color grading, sound design, and final mastering.',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop',
      capabilities: [
        'Scripted Series Production',
        'Talk Show Development',
        'Reality Format Creation',
        'Lifestyle Show Production',
        'Branded Content Creation',
        'Writing & Directing',
        'Professional Shooting',
        'Post-Production',
        'Color Grading',
        'Sound Design & Mastering'
      ]
    },
    {
      id: 2,
      title: 'Pay-Per-View / Event Monetization',
      icon: <FiGlobe className="w-8 h-8" />,
      description: 'Want to monetize a concert, boxing match, cultural festival, or conference? We provide full PPV solutions: secure streaming platforms, ticketing, subscriber access, DRM, analytics, and support.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      capabilities: [
        'Secure Streaming Platforms',
        'Event Ticketing Systems',
        'Subscriber Access Management',
        'Digital Rights Management (DRM)',
        'Audience Analytics',
        'Payment Processing',
        'Customer Support',
        'Multi-Device Compatibility',
        'Revenue Optimization',
        'Event Monetization'
      ]
    },
    {
      id: 3,
      title: 'Documentary & Factual Production',
      icon: <FiUsers className="w-8 h-8" />,
      description: 'Narrative-driven storytelling, investigative series, nature docs, cultural heritage. We help develop, shoot, and deliver documentaries that resonate with regional and international audiences.',
      image: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=600&h=400&fit=crop',
      capabilities: [
        'Narrative-Driven Storytelling',
        'Investigative Series',
        'Nature Documentaries',
        'Cultural Heritage Content',
        'Development & Research',
        'Professional Shooting',
        'Post-Production',
        'Regional Distribution',
        'International Audiences',
        'Factual Content Creation'
      ]
    },
    {
      id: 4,
      title: 'Distribution & Broadcasting',
      icon: <FiGlobe className="w-8 h-8" />,
      description: 'We facilitate distribution across TV stations, streaming services, and regional syndication. We also manage broadcast uplinks, SRT feeds, fiber circuits, satellite feeds, CDN delivery, on-demand platforms, and licensing.',
      image: 'https://images.unsplash.com/photo-1598550476439-6847785fcea6?w=600&h=400&fit=crop',
      capabilities: [
        'TV Station Distribution',
        'Streaming Service Integration',
        'Regional Syndication',
        'Broadcast Uplinks',
        'SRT Feeds',
        'Fiber Circuit Management',
        'Satellite Feeds',
        'CDN Delivery',
        'On-Demand Platforms',
        'Content Licensing'
      ]
    },
    {
      id: 5,
      title: 'Post Production & Technical Services',
      icon: <FiCamera className="w-8 h-8" />,
      description: 'Color grading, editing, VFX, motion graphics, audio mixing, closed captions, subtitling, format conversions (HD, UHD, 4K), archival services, versioning for multiple markets.',
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop',
      capabilities: [
        'Color Grading',
        'Professional Editing',
        'Visual Effects (VFX)',
        'Motion Graphics',
        'Audio Mixing',
        'Closed Captions',
        'Subtitling Services',
        'Format Conversions (HD, UHD, 4K)',
        'Archival Services',
        'Multi-Market Versioning'
      ]
    }
  ];

  const toggleSection = (id) => {
    setOpenSection(openSection === id ? null : id);
  };

  const servicesStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Caribbean Production & Broadcast Studio Services",
    "description": "Digital Shark Inc provides comprehensive production, broadcast, and streaming services across the Caribbean. Specializing in TV, Sports, Events & Pay-Per-View content.",
    "provider": {
      "@type": "Organization",
      "name": "Digital Shark Inc.",
      "url": "https://digitalshark.tv"
    },
    "areaServed": "Caribbean",
    "serviceType": [
      "Live Sports Production & Streaming",
      "Television, Series & Branded Content", 
      "Pay-Per-View / Event Monetization",
      "Documentary & Factual Production",
      "Distribution & Broadcasting",
      "Post Production & Technical Services"
    ]
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Services - Caribbean Production & Broadcast Studio"
        description="Complete production and broadcast solutions including live sports production, television series, Pay-Per-View systems, documentary production, distribution, and technical services across the Caribbean."
        keywords="Caribbean production services, broadcast studio, live sports production, television series production, Pay Per View systems, documentary production, distribution services, technical services, Barbados production company, Caribbean media services"
        url="/services"
        structuredData={servicesStructuredData}
      />
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-900 to-primary-800 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Services / What We Offer
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Complete production and broadcast solutions across the Caribbean
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Accordion */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="mb-8"
              >
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                  {/* Header */}
                  <button
                    onClick={() => toggleSection(service.id)}
                    className="w-full p-8 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6">
                        <div className="text-accent-500">
                          {service.icon}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-primary-800 mb-2">
                            {service.title}
                          </h3>
                          <p className="text-primary-600">
                            {service.description}
                          </p>
                        </div>
                      </div>
                      <motion.div
                        animate={{ rotate: openSection === service.id ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-primary-400"
                      >
                        <FiChevronDown size={24} />
                      </motion.div>
                    </div>
                  </button>

                  {/* Content */}
                  <AnimatePresence>
                    {openSection === service.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-8 pb-8">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Image */}
                            <div className="rounded-lg overflow-hidden">
                              <img
                                src={service.image}
                                alt={service.title}
                                className="w-full h-64 object-cover"
                              />
                            </div>

                            {/* Capabilities */}
                            <div>
                              <h4 className="text-lg font-semibold text-primary-800 mb-4">
                                Capabilities
                              </h4>
                              <div className="grid grid-cols-1 gap-2">
                                {service.capabilities.map((capability, capIndex) => (
                                  <div
                                    key={capIndex}
                                    className="flex items-center space-x-3 text-primary-600"
                                  >
                                    <div className="w-2 h-2 bg-accent-500 rounded-full flex-shrink-0"></div>
                                    <span className="text-sm">{capability}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Digital Shark Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
              Why Work With Us
            </h2>
            <p className="text-xl text-primary-600 max-w-3xl mx-auto">
              We combine deep regional knowledge with technical expertise to deliver exceptional results for every project.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: 'Deep regional knowledge + technical expertise',
                description: 'Combining local Caribbean insight with international production standards'
              },
              {
                title: 'End-to-end solutions (you don\'t have to juggle vendors)',
                description: 'Complete solutions from concept to broadcast, handling every aspect of production'
              },
              {
                title: 'Scalable for small shows to stadium events',
                description: 'Flexible infrastructure that adapts to events of any size and scope'
              },
              {
                title: 'Robust technical infrastructure & redundancy',
                description: 'Advanced streaming technology including Pay Per View systems for global reach'
              },
              {
                title: 'Creative, agile, and client-centric approach',
                description: 'Established relationships with broadcasters, federations, and cultural institutions'
              },
              {
                title: 'Global Distribution Network',
                description: 'Proven ability to deliver content to audiences worldwide across multiple platforms'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-primary-800 mb-3">
                  {item.title}
                </h3>
                <p className="text-primary-600 leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Services;
